import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * GEN-S1 §5 fields 2–3: the password box with the eye icon inside its
 * trailing end. The eye genuinely reveals and re-hides ("in the prototype this
 * icon is drawn but does nothing; make it work"), and the caps-lock edge case
 * shows its hint below the box.
 */
export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & { showCapsLockHint?: boolean }
>(function PasswordInput({ className, showCapsLockHint = true, onKeyDown, onKeyUp, ...props }, ref) {
  const { t } = useTranslation();
  const [visible, setVisible] = React.useState(false);
  const [capsLock, setCapsLock] = React.useState(false);

  const readCapsLock = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // getModifierState is the only reliable signal; it is read on both key
    // directions so toggling caps lock while focused updates the hint.
    setCapsLock(event.getModifierState('CapsLock'));
  };

  return (
    <div>
      <div className="relative">
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={cn('pe-11', className)}
          onKeyDown={(event) => {
            readCapsLock(event);
            onKeyDown?.(event);
          }}
          onKeyUp={(event) => {
            readCapsLock(event);
            onKeyUp?.(event);
          }}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? t('auth.hidePassword') : t('auth.showPassword')}
          title={visible ? t('auth.hidePassword') : t('auth.showPassword')}
          className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          {visible ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
        </button>
      </div>
      {showCapsLockHint && capsLock ? (
        <p className="mt-1 text-xs text-muted-foreground">{t('auth.capsLock')}</p>
      ) : null}
    </div>
  );
});
